<template>
    <fieldset :disabled="disabled">
        <div v-if="charities.length">
            <div class="alert alert-danger" v-if="displayErrors">
                <button aria-hidden="true" class="close" data-dismiss="alert" type="button">×</button>
                <h4><i class="icon fa fa-ban"></i> A validation error has occurred</h4>
                <ul>
                    <li
                        :key="error"
                        v-for="error in errors"
                        v-text="error"
                    ></li>
                </ul>
            </div>
            <ul class="list-group">
                <li
                    class="list-group-item no-padding"
                    :class="otherClasses.marginBottom"
                    v-for="(charity, index) in charities"
                    :key="charity.id"
                >
                    <donation-charity
                        :single-address="singleAddress"
                        :fundraising-page-id="fundraisingPageId"
                        :theme="theme"
                        v-bind="donationCharityProps(charity.id, index)"
                        @removeDonation="removeDonation"
                        @updateDonation="updateDonations"
                    ></donation-charity>
                </li>
            </ul>
            <div :class="boxClass.box" v-if="donationEntities.length">
                <div :class="boxClass.boxHeader">
                    <b-form-checkbox
                        id="anonymise_donor"
                        name="anonymise_donor"
                        :unchecked-value="false"
                        :value="true"
                        v-model="anonymiseDonor"
                    >
                        I would like to hide my name from public view.
                    </b-form-checkbox>
                    <hr>
                    <div v-if="singleAddress && oneOrMoreCharitiesAllowGiftAid">
                        <p>I am a UK tax payer and this donation is gift aid eligible</p>
                        <b-form-radio-group
                            stacked
                            v-model="userHasChosenToGiftAid"
                            :options="[
                            {
                              text: 'Yes',
                              value: true,
                            },
                            {
                              text: 'No',
                              value: false,
                            }
                        ]"
                            data-vv-as="field"
                            name="gift_aid"
                            v-validate="'required'"
                            :state="validateState('gift_aid')"
                        >
                            <b-form-invalid-feedback :state="validateState('gift_aid')" class="text-danger">You must
                                select either Yes or No to gift aid
                            </b-form-invalid-feedback>
                        </b-form-radio-group>
                    </div>
                </div>
                <div :class="boxClass.boxBody"
                     v-if="(userHasChosenToGiftAid !== null || !oneOrMoreCharitiesAllowGiftAid) &&
                     ((giftAidAddressFields.length > 1 && userHasChosenToGiftAid )|| nonGiftAidAddressFields.length > 1)"
                >
                    <div>
                        <p v-if="userHasChosenToGiftAid" style="font-style: italic;">By clicking yes, you agree that you are
                            a
                            UK taxpayer
                            and understand that if you pay less Income Tax and/or Capital Gains Tax in the current tax
                            year than the
                            amount of Gift Aid claimed on all your donations it is your responsibility to pay any
                            difference. If you gift aid your donation, you are also consenting to the details being used
                            by the charity to claim gift aid on your behalf.
                        </p>
                    </div>
                    <donation-address
                        :fields="userHasChosenToGiftAid ? giftAidAddressFields : nonGiftAidAddressFields"
                        :name-is-required="nameIsRequired"
                        @updateAddress="updateAddress"
                        v-bind="donationAddress"
                        v-if="singleAddress"
                    ></donation-address>
                </div>
                <div :class="boxClass.boxFooter" v-if="submittable">
                    <button
                        class="btn btn-submit btn-block btn-success"
                        :class="{'disabled': !userEntity}"
                        :disabled="submitting"
                        @click="addDonations"
                    >
                        Submit
                    </button>
                </div>
            </div>
        </div>
    </fieldset>
</template>

<script>
import DonationAddress from "./Address.vue";
import DonationCharity from "./Default.vue";
import axios from "axios";
import BFormGroup from "bootstrap-vue/es/components/form-group/form-group";
import BFormCheckbox from "bootstrap-vue/es/components/form-checkbox/form-checkbox";

const GIFT_AID_ADDRESS_FIELDS = ['first_name', 'last_name', 'email', 'line_1', 'post_code'];
const NON_GIFT_AID_ADDRESS_FIELDS = ['first_name', 'last_name'];

export default {
    inject: ['$validator'],
    components: {
        BFormCheckbox, BFormGroup,
        DonationAddress,
        DonationCharity,
    },

    props: {
        charities: {
            type: Array,
            required: true,
        },
        disabled: {
            required: false,
            type: Boolean,
        },
        fundraisingPageId: {
            default: null,
            required: false,
            type: Number,
        },
        nameIsRequired: {
            default: false,
            required: false,
            type: Boolean,
        },
        singleAddress: {
            default: true,
            required: false,
            type: Boolean,
        },
        submittable: {
            default: true,
            required: false,
            type: Boolean,
        },
        theme: {
            type: String,
            default: 'bs4',
            required: false,
        },
        userEntity: {
            required: true,
            type: [Object, Boolean],
        },
        value: {
            default: () => ([]),
            required: false,
            type: Array,
        },
        occasionId: {
            required: false,
            type: Number,
        },
    },

    data() {
        return {
            anonymiseDonor: false,
            displayErrors: false,
            donationAddress: {
                first_name: this.userEntity && this.userEntity.first_name
                    ? this.userEntity.first_name
                    : '',
                last_name: this.userEntity && this.userEntity.last_name
                    ? this.userEntity.last_name
                    : '',
                email: this.userEntity && this.userEntity.email
                    ? this.userEntity.email
                    : '',
                country: null,
                county: null,
                line_1: null,
                line_2: null,
                post_code: null,
                town: null,
            },
            donations: this.reverseEngineer(this.value),
            errors: [],
            submitting: false,
            userHasChosenToGiftAid: this.value.length
                ? this.value.filter(donation => donation.gift_aid).length > 0
                : null,
        }
    },
    computed: {
        boxClass() {
            if (this.theme == 'bs3') {
                return {
                    box: 'panel panel-default',
                    boxHeader: 'panel-heading',
                    boxBody: 'panel-body',
                    boxFooter: 'panel-footer',
                    padding: ''
                }
            } else if (this.theme == 'evt') {
                return {
                    box: 'box box-default',
                    boxHeader: 'box-header with-border',
                    boxBody: 'box-body',
                    boxFooter: 'box-footer',
                    padding: ''
                }
            } else {
                return {
                    box: 'card card-default',
                    boxHeader: 'card-header',
                    boxBody: 'card-body',
                    boxFooter: 'card-footer',
                    padding: 'px-0 py-0'
                }
            }
        },
        donationEntities() {
            return Object.entries(this.donations).map(([charityId, donation]) => {
                let integerCharityId = parseInt(charityId),
                    donationEntity = {
                        ...donation,
                        anonymise_donor: this.anonymiseDonor,
                        charity_id: integerCharityId,
                        user_id: this.userEntity.id,
                        occasion_id: this.occasionId,
                    }
                ;

                if (this.singleAddress) {
                    let isGiftAidableCharity = this.giftAidableCharityIds.includes(integerCharityId),
                        donationAddressEntries = Object.entries(this.donationAddress).filter(([key, value]) => {
                            let addressFields = isGiftAidableCharity
                                ? ['email', 'first_name', 'last_name', ...this.giftAidAddressFields]
                                : ['email', 'first_name', 'last_name', ...this.nonGiftAidAddressFields];
                            return addressFields.includes(key);
                        }),
                        donationAddress = Object.fromEntries(donationAddressEntries)
                    ;

                    Object.assign(donationEntity, {
                        ...donationAddress,
                        gift_aid: Boolean(this.userHasChosenToGiftAid) && isGiftAidableCharity,
                    });
                }

                return donationEntity;
            });
        },
        giftAidableCharityIds() {
            return this.charities
                .filter(charity => charity.gift_aid)
                .map(charity => charity.id);
        },
        oneOrMoreCharitiesAllowGiftAid() {
            return this.donationEntities
                .filter(donationEntity => this.giftAidableCharityIds.includes(donationEntity.charity_id))
                .length > 0;
        },
        otherClasses() {
            if (this.theme == 'bs3') {
                return {
                    marginBottom: 'margin-bottom',
                    checkboxFix: '',
                }
            } else if (this.theme == 'evt') {
                return {
                    marginBottom: 'margin-bottom',
                    checkboxFix: 'no-margin',
                }
            } else {
                return {
                    marginBottom: 'mb-4',
                    checkboxFix: '',
                }
            }
        },
        giftAidAddressFields() {
            return GIFT_AID_ADDRESS_FIELDS.filter(field => !this.userEntity[field]) //Don't show more fields that required.
        },
        nonGiftAidAddressFields() {
            return NON_GIFT_AID_ADDRESS_FIELDS.filter(field => !this.userEntity[field]) //Don't show more fields that required
        }
    },

    watch: {
        donationEntities: {
            deep: true,
            handler(newVal) {
                if (!this.submittable) {
                    this.$emit(`input`, newVal);
                }
            },
        },
        errors(newVal, oldVal) {
            if (!oldVal.length && newVal.length) {
                this.displayErrors = true;
            } else if (oldVal.length && !newVal.length) {
                this.displayErrors = false;
            }
        },
        oneOrMoreCharitiesAllowGiftAid(newVal, oldVal) {
            if (!newVal && oldVal) {
                Object.assign(this, {userHasChosenToGiftAid: null});
            }
        }
    },

    methods: {
        addDonations() {
            this.errors = [];
            this.submitting = true;
            this.$validator.validateAll().then((result) => {
                if (result) {
                    axios
                        .post(`/api/v0/donations/add`, {
                            donations: this.donationEntities,
                            user_id: this.userEntity.id,
                        })
                        .then((response) => {
                            let status = response.data.status;
                            if (status == "error") {

                                let errors = response.data.data.errors;

                                //Inject errors to validator
                                errors.forEach((error) => {
                                    let keys = Object.keys(error);
                                    keys.forEach(key => {
                                        let innerError = error[key];
                                        let field = key;
                                        let msg = Object.values(innerError)[0];
                                        this.vErrors.add({
                                            field: field,
                                            msg: msg
                                        });
                                    })
                                });
                                this.submitting = false;
                            } else if (status == "success") {
                                //Redirect to basket
                                window.location = response.data.data.redirect
                            }
                        }).catch((response) => {
                        this.submitting = false;
                    })
                } else {
                    this.submitting = false;
                }
            });
        },
        donationCharityProps(charityId, charityIndex) {
            let props = Object.assign({}, {
                charity: this.charities[charityIndex],
            });

            if (this.donationIsSetForCharity(charityId)) {
                Object.assign(props, {
                    donation: this.donations[charityId],
                });
            }

            return props;
        },
        donationIsSetForCharity(charityId) {
            return Boolean(this.donations[charityId]);
        },
        removeDonation(charityId) {
            Vue.delete(this.donations, charityId);
        },
        reverseEngineer(donationEntitiesArray) {
            if (!donationEntitiesArray.length) return {};
            let donationsEntries = donationEntitiesArray
                .map(donationEntity => {
                    let
                        {charity_id, amount, id} = donationEntity,
                        fields = this.singleAddress
                            ? NON_GIFT_AID_ADDRESS_FIELDS
                            : GIFT_AID_ADDRESS_FIELDS,
                        donation = Object.fromEntries(fields.map(field => [field, donationEntity[field]]))
                    ;

                    return [charity_id, Object.assign(donation, {amount, id})];
                });

            return Object.fromEntries(donationsEntries);
        },
        updateAddress(newVals) {
            let donationAddress = Object.assign({}, this.donationAddress, newVals);
            Object.assign(this, {donationAddress});
        },
        updateDonations(object) {
            let {charityId, donation} = object;
            if (this.donationIsSetForCharity(charityId)) {
                donation = Object.assign({}, this.donations[charityId], donation);
            }
            if (!donation.fundraisingPageId) {
                donation = Object.assign(donation, {
                    additional_answers: {},
                    fundraising_page_id: this.fundraisingPageId,
                });
            }
            Vue.set(this.donations, charityId, donation);
        },
        validateState(ref) {
            if (
                this.vFields[ref] &&
                (this.vFields[ref].dirty || this.vFields[ref].validated)
            ) {
                return !this.vErrors.has(ref);
            }
            return null;
        },
    },
}
</script>



// WEBPACK FOOTER //
// src/Assets/components/Donations/Form/Bulk.vue